"use client";

import { useEffect, useState } from "react";
import type { TopSellersFilterState } from "@/features/topsellers/types";
import {
  getDefaultFilterState,
  hasActiveFilters,
} from "@/features/topsellers/utils";

function loadFromStorage(key: string): TopSellersFilterState {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return getDefaultFilterState();
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && "upcomingOnly" in parsed) {
      return parsed as TopSellersFilterState;
    }
  } catch {
    /* ignore */
  }
  return getDefaultFilterState();
}

export function usePersistedFilters(key?: string) {
  const [filters, setFilters] = useState<TopSellersFilterState>(() =>
    key && typeof window !== "undefined"
      ? loadFromStorage(key)
      : getDefaultFilterState()
  );

  useEffect(() => {
    if (!key || typeof window === "undefined") return;
    const restored = loadFromStorage(key);
    const hasStored =
      JSON.stringify(restored) !== JSON.stringify(getDefaultFilterState());
    if (!hasStored) return;
    queueMicrotask(() => {
      setFilters((prev) => {
        const isDefault =
          JSON.stringify(prev) === JSON.stringify(getDefaultFilterState());
        return isDefault ? restored : prev;
      });
    });
  }, [key]);

  useEffect(() => {
    if (!key || typeof window === "undefined") return;
    if (hasActiveFilters(filters)) {
      sessionStorage.setItem(key, JSON.stringify(filters));
    } else {
      sessionStorage.removeItem(key);
    }
  }, [key, filters]);

  return [filters, setFilters] as const;
}
